'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { Box, Button } from '@ui-library';
import { CheckOutSettings, ReservationWithRateDetailsDTO } from '@/models';

import { getCheckOutAvailability } from '../helper';
import Link from 'next/link';

interface CheckOutButtonProps {
  locale: string;
  company: string;
  companyTimezone: string;
  guestBookSlug: string;
  reservation: ReservationWithRateDetailsDTO;
  primaryColor: string;
  checkOutSettings: CheckOutSettings | null;
  guestId?: string;
}

const CheckOutButton = ({
  locale,
  company,
  companyTimezone,
  guestBookSlug,
  reservation,
  primaryColor,
  checkOutSettings,
  guestId,
}: CheckOutButtonProps): React.ReactElement | null => {
  const intl = useIntl();

  const { canCheckOut, isDepartureDay } = getCheckOutAvailability(
    reservation,
    companyTimezone,
  );
  const shouldBlockChannelCollect =
    !checkOutSettings?.allow_channel_collect && reservation.isChannelCollect;

  // only the primary guest can check out
  const isPrimaryGuest = guestId === reservation.guestId;

  if (
    !canCheckOut ||
    !isDepartureDay ||
    !checkOutSettings?.enabled ||
    shouldBlockChannelCollect ||
    !isPrimaryGuest
  ) {
    return null;
  }

  const checkOutOptions = [
    {
      label: intl.formatMessage({
        id: 'check_out_label/Check-out',
        defaultMessage: 'Check-Out',
      }),
      value: 'CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Online_Check-out',
        defaultMessage: 'Online Check-Out',
      }),
      value: 'ONLINE_CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Self_Check-out',
        defaultMessage: 'Self Check-Out',
      }),
      value: 'SELF_CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Express_Check-out',
        defaultMessage: 'Express Check-Out',
      }),
      value: 'EXPRESS_CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Mobile_Check-out',
        defaultMessage: 'Mobile Check-Out',
      }),
      value: 'MOBILE_CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Digital_Check-out',
        defaultMessage: 'Digital Check-Out',
      }),
      value: 'DIGITAL_CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Complete_Check-out',
        defaultMessage: 'Complete Check-Out',
      }),
      value: 'COMPLETE_CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Check-out_Now',
        defaultMessage: 'Check-Out Now',
      }),
      value: 'CHECK_OUT_NOW',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Begin_Check-out',
        defaultMessage: 'Begin Check-Out',
      }),
      value: 'BEGIN_CHECK_OUT',
    },
    {
      label: intl.formatMessage({
        id: 'check_out_label/Start_Check-out',
        defaultMessage: 'Start Check-Out',
      }),
      value: 'START_CHECK_OUT',
    },
  ];

  const checkoutButtonLabel =
    checkOutOptions.find(
      (option) => option.value === checkOutSettings?.check_out_btn_label,
    )?.label ||
    intl.formatMessage({
      id: 'common/checkout_label',
      defaultMessage: 'Check-Out',
    });

  return (
    <Link
      prefetch={true}
      href={`/${locale}/${company}/${guestBookSlug}/reservations/${reservation.id}/check-out`}
    >
      <Box
        display="flex"
        width="100%"
        marginBottom="1rem"
        justifyContent="center"
        fontSize="1rem"
      >
        <Button
          width="100%"
          display="block"
          textAlign="center"
          maxWidth="336px"
          size="lg"
          colorPalette={primaryColor}
          style={{ backgroundColor: primaryColor }}
          variant="solid"
        >
          {checkoutButtonLabel}
        </Button>
      </Box>
    </Link>
  );
};

export default CheckOutButton;
